
import { memo, useMemo } from "react"
import { cn } from "../../utils/cn"
import { useCalendarStore } from "../../stores/calendarStore"
import { useUserPreferencesStore } from "../../stores/userPreferencesStore"
import { usePermissions } from "../../hooks/usePermissions"
import { format } from "date-fns"
import { getColorScheme } from "../../utils/colorSchemes"
import { useEventsStore } from "../../stores/eventsStore"
import { getTypeColor } from "../../utils/eventTypeColors"

interface DayCellProps {
  date: Date
  isCurrentMonth: boolean
  isToday: boolean
  selectedChild: string
  isSelected?: boolean
  onClick: (date: Date) => void
  onDoubleClick?: (date: Date) => void
  onMouseDown: (date: Date) => void
  onMouseEnter: (date: Date) => void
  isDragSelected: boolean
}

export const DayCell = memo(function DayCell({
  date,
  isCurrentMonth,
  isToday,
  selectedChild: _selectedChild, // Oznaczamy jako nieużywane, aby uniknąć błędu TS6133
  isSelected,
  onClick,
  onDoubleClick,
  onMouseDown,
  onMouseEnter,
  isDragSelected
}: DayCellProps) {
  // Sprawdź czy to weekend (sobota = 6, niedziela = 0)
  const isWeekend = date.getDay() === 0 || date.getDay() === 6
  const day = date.getDate()
  
  // Pobierz preferencje użytkownika i uprawnienia
  const schemeData = useUserPreferencesStore(state => getColorScheme(state.preferences.color_scheme))
  const { canEditCalendar, isObserver } = usePermissions()
  
  // Pobierz dane bezpośrednio ze store dla tego konkretnego dnia
  const dateString = useMemo(() => format(date, 'yyyy-MM-dd'), [date])
  // Wydarzenia dla tego dnia (po starcie)
  const dayEvents = useEventsStore(
    state => state.events.filter(ev => format(new Date(ev.start_at), 'yyyy-MM-dd') === dateString),
  )
  // Filtruj ukryte wpisy dla obserwatora
  const visibleEvents = useMemo(() => dayEvents.filter(ev => !(ev.parents_only && isObserver)), [dayEvents, isObserver])
  // Licz tylko notatki (dawne wydarzenia). Uwzględnij legacy 'event' jako notatkę.
  const noteCount = visibleEvents.filter(ev => (ev.type === 'note' || ev.type === 'event')).length
  // Licz koszty
  const expenseCount = visibleEvents.filter(ev => ev.type === 'expense').length
  const hasAllDay = visibleEvents.some(ev => ev.all_day)
  
  // Używamy selektora z równością dla lepszej optymalizacji
  const dayEntries = useCalendarStore(
    state => state.entries.filter(entry => entry.date === dateString),
    (a, b) => {
      // Porównaj tylko entries dla tego konkretnego dnia
      if (a.length !== b.length) return false
      return a.every((entry, index) => 
        entry.id === b[index]?.id && 
        entry.location === b[index]?.location && 
        entry.comment === b[index]?.comment
      )
    }
  )

  // Jeden wpis dzienny – jeśli istnieje, bierzemy pierwszy
  const dayEntry = dayEntries[0]
  const location = dayEntry?.location || null
  const comment = dayEntry?.comment || null
  
  const scheme = useUserPreferencesStore(state => getColorScheme(state.preferences.color_scheme))
  // Akcent oparty o kolor Notatki w bieżącym schemacie, spójny z legendą typów
  const accentColor = useMemo(() => getTypeColor('note', scheme.id), [scheme.id])
  
  const handleClick = () => {
    onClick(date)
  }
  
  const handleMouseDown = () => {
    onMouseDown(date)
  }
  
  const handleMouseEnter = () => {
    onMouseEnter(date)
  }
  
  const handleDoubleClick = () => {
    if (onDoubleClick) onDoubleClick(date)
  }
  
  const getDayStyle = () => {
    // Pokazujemy tylko rzeczywiste przypisania z bazy danych
    if (location === "home_a") {
      return { backgroundColor: schemeData.homeA.color }
    } else if (location === "home_b") {
      return { backgroundColor: schemeData.homeB.color }
    } else {
      // Dla weekendów dodaj delikatne tło tylko jeśli nie ma przypisania domu
      if (isWeekend) {
        return { backgroundColor: 'rgba(248, 250, 252, 0.8)' } // Delikatny szary
      }
      return { backgroundColor: 'rgba(255, 255, 255, 0.8)' }
    }
  }
  
  const getDayTitle = () => {
    if (location === "home_a") {
      return "Dom A"
    } else if (location === "home_b") {
      return "Dom B"
    } else {
      return "Nie przypisano"
    }
  }
  
  const hasComment = () => {
    return comment !== null && comment !== ""
  }

  return (
    <div
      className={cn(
        "day-cell p-1 sm:p-2 md:p-3 relative select-none",
        canEditCalendar
          ? "cursor-pointer hover:opacity-90 active:opacity-80"
          : "cursor-not-allowed opacity-75",
        "transition-opacity duration-100",
        isCurrentMonth ? "" : "opacity-70",
        isDragSelected && "ring-1 ring-blue-400",
        !isCurrentMonth && "opacity-50",
        // Zaznaczenie wybranego dnia – obwódka (outline nie jest obcinany przez overflow:hidden)
        isSelected && "outline outline-3 outline-blue-600 -outline-offset-[3px]",
        // Aplikuj klasę weekend tylko jeśli nie ma przypisania domu
        isWeekend && !location && "weekend-cell"
      )}
      style={getDayStyle()}
      onClick={handleClick}
      onDoubleClick={handleDoubleClick}
      onMouseDown={handleMouseDown}
      onMouseEnter={handleMouseEnter}
      title={getDayTitle()}
    >
      {/* Pasek all-day na górze */}
      {hasAllDay && (
        <span className="absolute top-0 left-0 right-0 h-[4px] rounded-sm" aria-hidden="true" style={{ backgroundColor: accentColor }}></span>
      )}
      <div className="flex justify-center items-center">
        <span
          className={cn(
            "inline-block text-xs sm:text-sm font-medium rounded-full w-5 h-5 sm:w-6 sm:h-6 flex items-center justify-center transition-colors duration-200",
            isToday ? "bg-blue-500 text-white shadow-[0_0_0_2px_rgba(59,130,246,0.3)]" : (
              location ? "text-white" : "text-slate-700"
            )
          )}
        >
          {day}
        </span>
      </div>

      {/* Kółko z liczbą notatek w lewym dolnym rogu */}
      {noteCount > 0 && (
        <span
          className={cn(
            "absolute bottom-1 left-1 rounded-full text-white text-center font-semibold",
            "flex items-center justify-center",
            "h-[20px] min-w-[20px] px-[4px]",
            noteCount >= 10 ? "text-[11px] leading-[20px]" : "text-[12px] leading-[20px]"
          )}
          style={{ backgroundColor: accentColor }}
          aria-hidden="true"
        >
          {noteCount >= 10 ? '+' : noteCount}
        </span>
      )}

      {/* Kółko z liczbą kosztów w prawym dolnym rogu */}
      {expenseCount > 0 && (
        <span
          className={cn(
            "absolute bottom-1 right-1 rounded-full text-white text-center font-semibold",
            "flex items-center justify-center",
            "h-[20px] min-w-[20px] px-[4px]",
            expenseCount >= 10 ? "text-[11px] leading-[20px]" : "text-[12px] leading-[20px]"
          )}
          style={{ backgroundColor: getTypeColor('expense', scheme.id) }}
          aria-hidden="true"
        >
          {expenseCount >= 10 ? '+' : expenseCount}
        </span>
      )}

      {/* Wskaźnik komentarza (przesuń jeśli jest kółko wydarzeń) */}
      {hasComment() && (
        <div className={cn(
          "absolute bottom-1 w-2 h-2 bg-yellow-400 rounded-full transition-opacity duration-200",
          expenseCount > 0 ? "right-6" : "right-1"
        )}></div>
      )}
    </div>
  )
}) 